import React, { useEffect, useRef, useState } from 'react'
import { StoreLocatorItemType } from '@next-nx/utils/types'
import { useTealium } from '@next-nx/hooks';
import { getTimestamp } from '@next-nx/utils';
import { ChevronDown, ChevronUp, Email, Time, Phone, Print, ArrowRight, UnipolserviceIllustration } from '@next-nx/shared-ui/icons'
import { Button } from '@next-nx/shared-ui/atoms'
import styles from './StoreLocatorResultsItem.module.sass'
import { useTrans } from '@next-nx/hooks'
import { CENTRO_GOMMISTA, CENTRO_OFFICINA } from '@next-nx/utils/settings';


interface Props {
  type: string
  item: StoreLocatorItemType
  selected: boolean
  setSelected: (v: boolean) => void
  option?: any
  ctaLabel: string
}

const StoreLocatorResultsItem = (props: Props) => { 

  const {
    type = '',
    item = undefined,
    selected = false,
    setSelected = () => undefined,
    option = undefined,
    ctaLabel = '',
  } = props

  const t = useTrans()
  const [open, setOpen] = useState(selected)
  const innerContentRef = useRef(null)
  const { linkDataLayer } = useTealium()

  const handleTracking = (action_type?: string|number, cta?: string, info?: string) => {
    if(typeof action_type === 'number' && action_type > 2 ) {
      return
    }

    const action_timestamp = getTimestamp()
    let name = type === 'agenzie' ? type : type === 'centri_convenzionati' ? 'strutture_convenzionate' : 'filiali'

    if (typeof action_type === 'number') {
      action_type = action_type === 0 ? 'chiama' : action_type === 1 ? 'stampa' : action_type === 2 ? 'invio_email' : undefined
    }
      
    linkDataLayer({
      virtual: 'action',
      action_name: 'storelocator', 
      action_tech: 'click',
      action_type: `${name}_${action_type}`,
      action_timestamp,
      action_effect: `${name}_interazione`,
      cta: cta?.replace(/ /g, '_').replace(/,/g, '').toLowerCase(),
      info: info?.replace(/ /g, '_').replace(/,/g, '').replace(/\./g, '').toLowerCase(),
    })
  }

  useEffect(() => {
    if (selected)
      setOpen(true)
  }, [selected])

  const details = item ? [
    ...item.telefono ? [{ key: 'telefono', icon: <Phone />, value: <a href={`tel:${item.telefono}`}>{item.telefono}</a> }] : [],
    ...item.fax ? [{ key: 'fax', icon: <Print />, value: <a href={`fax:${item.fax}`}>{item.fax}</a> }] : [],
    ...item.email ? [{ key: 'email', icon: <Email />, value: <a href={`mailto:${item.email}`}>{item.email}</a> }] : [],
    ...item.orari ? [{ key: 'orari', icon: <Time />, value: <span>{item.orari}</span> }] : [],
  ] : []

  const nomiMarche = item?.marche?.edges.map(x => x.node).map(x => x.marca) || []

  return item ? (
    <div className={`${styles.root} ${selected ? styles['root--selected'] : ''}`}>
      {!!item.isUnipolservice && (
        <div className={styles.convenzionata}>
          <span className={styles.convenzionata__title}>Convenzionata</span>
          <UnipolserviceIllustration />
        </div>
      )}
      <div className={`${styles.top}`}>
        <button role="button" className={styles.title} onClick={() => setSelected (true)}>{item.ragioneSociale}</button>
        {type !== "filiali" && type !== "agenzie" && (
          <span className={styles.distanza}>{(Math.round((item.distance || 0) * 100) / 100).toFixed(2)} km</span>
        )}
      </div>
      <p className={styles.address}>
        {!!item.indirizzo && <>{item.indirizzo}<br /></>}
        {item.cap} {item.comune} {!!item.provincia && <>({item.provincia})</>}
      </p>
      {type === "filiali" || type === "agenzie" ?
      details.length > 0 && (
        <>
          <div className={styles.detailsTrigger}>
            <Button
              label={t('Dettagli')}
              icon={open ? <ChevronUp /> : <ChevronDown />}
              iconPos="right"
              className={styles.detailsTrigger}
              variant="ghost"
              color="blue"
              onClick={() => {
                handleTracking(
                  open ? 'chiudi_dettagli' : 'apri_dettagli',
                  'elenco',
                  item.ragioneSociale
                )
                setOpen(!open)
                setSelected(true)
              }}
              size="sm"
            />
          </div>
          <div className={styles.details} style={{
            maxHeight: innerContentRef?.current && open ? innerContentRef.current.clientHeight : 0,
          }}>
            <div className={styles.detailsInnerContent} ref={innerContentRef}>
              {details.map((x, i) => (
                <div className={styles.row} key={x.key} onClick={() => handleTracking(i, 'elenco', item.ragioneSociale)}>
                  <span className={styles.rowIcon}>{x.icon}</span>
                  <span className={styles.rowValue}>{x.value}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <>
          <div className={styles.details}>
            <div className={styles.detailsInnerContent}>
              {details.map(x => (
                <div className={styles.row} key={x.key} onClick={() => handleTracking('chiama', 'elenco', item.ragioneSociale)}>
                  <span className={styles.rowIcon}>{x.icon}</span>
                  <span className={styles.rowValue}>{x.value}</span>
                </div>
              ))}
            </div>
          </div>
          {option && [CENTRO_GOMMISTA, CENTRO_OFFICINA].includes(option.nomeStatico) && nomiMarche && nomiMarche.length > 0 ? (
          <>
            <div className={styles.marchiTrigger}>
              <Button label={t("Marchi autorizzati")} icon={open ? <ChevronUp /> : <ChevronDown />} iconPos="right" className={styles.detailsTrigger} variant="ghost" color="blue" onClick={() => {setOpen(!open), handleTracking('marchi_autorizzati', 'elenco', item.ragioneSociale)}} size="sm"/>
            </div>
            <div className={styles.marchi} style={{
              maxHeight: innerContentRef?.current && open ? innerContentRef.current.clientHeight : 0,
            }}>
              <div className={styles.marchiInnerContent} ref={innerContentRef}>
                {nomiMarche.map((x, i) => (
                  <span key={i} className={styles.rowValue}>{x}{i < nomiMarche.length - 1 ? ', ' : ''}</span>
                ))}
              </div>
            </div>
          </>
          ) : null}
        </>
      )}
      <div className={styles.bottom}>
        {!!ctaLabel && <Button href={item.url} label={ctaLabel} variant="ghost" icon={<ArrowRight />} iconPos="right" className={styles.cta} onClick={() => handleTracking(1, 'vai_alla_filiale', 'elenco', item.ragioneSociale)} size="sm" />}
        {!!item.telefono && <Button icon={<Phone />} href={`tel:${item.telefono}`} onClick={() => handleTracking('chiama', 'elenco', item.ragioneSociale)} size="sm" />}
        {!!item.email && <Button icon={<Email />} href={`mailto:${item.email}`} onClick={() => handleTracking('invia_email', 'elenco', item.ragioneSociale)} size="sm" />}
      </div>
    </div>
  ) : <></>
}

export default StoreLocatorResultsItem

